<template>
  <div>
    <div class="form-group">
      <Typeahead
        v-model="counterParty"
        label="Who is the Counterparty? (Business name)"
        :items="allAvailableCounterParties"
        :errors="$page.props.errors.counter_party_id"
        placeholder="Start typing to search..."
        :tolerance="0"
        return-field="id"
        id-field="id"
        search-field="name"
        display-field="name"
        @selected="counterPartySelected"
        @noResults="noCounterparty"
      >
        <template v-slot:item="slotProps">
          <span v-if="slotProps.match.name">
            {{ slotProps.match.name }}
            <br />
          </span>
          <strong v-if="slotProps.match.address"><small>Address: {{ slotProps.match.address }}</small></strong>
          <strong v-if="slotProps.match.phone"><small>Phone: {{ slotProps.match.phone }}</small></strong>
        </template>
        <template v-slot:noResults>
          <h6 class="mt-2">Can’t find your Counterparty?</h6>
          <small>
            <p class="mb-2">Are you sure there is an Master Services Agreement (MSA) for this Counterparty on Clausify? If not, please <span class="a">Create an MSA first</span>. Once the MSA is executed, you can configure a Work Category for that Counterparty. </p>
          </small>
        </template>
      </Typeahead>
      <div class="row">
        <small class="form-text form-helper col-md-9 mt-2">
          Can’t find the Counterparty you’re looking for? <InertiaLink :href="route('asset.create', 'master-service-agreement')">Click here</InertiaLink> to set up a Master Services Agreement (MSA) for the Counterparty. This is mandatory to set up any Contracts for a Counterparty.
        </small>
      </div>
    </div>

    <div v-if="form.counter_party_id">
      <div class="form-group">
        <Choice
          v-model="form.role"
          @selected="msaSelected"
          :errors="$page.props.errors.role"
          label="What is your organisation's role?"
          :items="msaChoices"
        />
      </div>
    </div>

    <div v-if="form.counter_party_id && form.asset_id && configuredWorkCategories">
      <div class="form-group">
        <label>Select a Work Category to configure</label>
        <div class="row">
          <small class="form-text form-helper col-md-9">
            <p>Each of the follow Work Categories (WC) can be configured once per Counterparty. Once configured a WC can be used for multiple Statements of Work (SOWs) for that Counterparty.</p>
            <p>For example, once you create a Work Category for Consulting Services, it can be used for all Consulting Services SOWs for that Counterparty.</p>
          </small>
        </div>

        <div v-for="(group, index) in groupedWorkCategories" :key="index">
          <label class="text-purple">{{ index }}</label>
          <div class="row">
            <div class="col-6" v-for="workCategory in group" :key="workCategory.id">
              <Radio
                :label="workCategory.label"
                v-model="form.work_category_type_id"
                @input="$page.props.errors.work_category_type_id = null"
                :val="workCategory.id"
                :disabled="fetchingConfiguredWorkCategories || configuredWorkCategories.includes(workCategory.id) || disableInputs"
              />
            </div>
          </div>
          <hr
            v-if="Object.keys(groupedWorkCategories)[Object.keys(groupedWorkCategories).length - 1] !== index"
            class="my-4"
          />
        </div>
        <Error
          v-if="$page.props.errors.work_category_type_id && $page.props.errors.work_category_type_id.length"
          :errors="$page.props.errors.work_category_type_id"
        />
      </div>

      <div class="form-group">
        <Choice
          v-model="form.disaster_plan"
          @selected="$page.props.errors.disaster_plan = []"
          :errors="$page.props.errors.disaster_plan"
          label="Will the Supplier be required to produce a Disaster Plan in relation to Work performed under this Work Category?"
          :grow="false"
          :items="disasterPlanItems"
        />
      </div>

      <div class="form-group">
        <Choice
          v-model="form.benchmark"
          @selected="$page.props.errors.benchmark = []"
          :errors="$page.props.errors.benchmark"
          label="Does the Customer require the right to benchmark the Supplier’s Work performed under this Work Category?"
          :grow="false"
          :items="benchmarkItems"
        />
      </div>
    </div>
  </div>
</template>

<script>
import mixin from './mixin';

export default {
  mixins: [mixin],

  props: {
    workCategories: Array,
  },

  data() {
    return {
      form: {
        counter_party_id: this.asset ? this.asset.counter_party_id : null,
        counter_party_type: this.asset ? this.asset.counter_party_type : null,
        role: this.asset ? this.asset.role : null,
        asset_id: this.asset ? this.asset.asset_id : null,
        work_category_type_id: this.asset ? this.asset.work_category_type_id : null,

        disaster_plan: this.asset ? this.asset.fields.disaster_plan : null,
        benchmark: this.asset ? this.asset.fields.benchmark : null,
      },
      fetchingConfiguredWorkCategories: true,
      configuredWorkCategories: [],
      disasterPlanItems: [
        {
          label: 'Yes',
          value: true,
        }, {
          label: 'No',
          value: false,
        },
      ],
      benchmarkItems: [
        {
          label: 'Yes',
          value: true,
        }, {
          label: 'No',
          value: false,
        },
      ],
      supplierMsa: null,
      customerMsa: null,
    };
  },

  computed: {
    state() {
      return this.$store.state.assetState;
    },

    disableInputs() {
      if (!this.workCategory) return false;

      return this.state.state !== 'editing';
    },

    groupedWorkCategories() {
      const groupBy = (key) => (array) => array.reduce((objectsByKeyValue, obj) => {
        const value = obj[key];
        // eslint-disable-next-line no-param-reassign
        objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
        return objectsByKeyValue;
      }, {});
      const groupByCategory = groupBy('group');
      return groupByCategory(this.workCategories);
    },

    msaChoices() {
      return [
        {
          label: 'Selling goods and/or services (Supplier)',
          value: this.supplierMsa ? 'supplier' : '',
        }, {
          label: 'Buying goods and/or services (Customer)',
          value: this.customerMsa ? 'customer' : '',
        },
      ];
    },
  },

  methods: {
    counterPartySelected(cp) {
      this.$page.props.errors.counter_party_id = null;

      if (cp) {
        this.form.counter_party_id = cp.id;
        this.form.counter_party_type = cp.type;
        this.fetchExecutedMasterServiceAgreements();
      } else {
        this.form.counter_party_id = null;
        this.form.counter_party_type = null;
        this.form.asset_id = null;
        this.supplierMsa = null;
        this.customerMsa = null;
      }
    },

    msaSelected() {
      this.form.asset_id = this.form.role === 'supplier' ? this.supplierMsa.id : this.customerMsa.id;

      this.configuredWorkCategories = [];
      this.fetchConfiguredWorkCategories();
      this.$page.props.errors.asset_id = [];
    },

    fetchExecutedMasterServiceAgreements() {
      window.axios
        .get(this.route('counter-party.asset.index', [this.form.counter_party_id, 'master-service-agreement']))
        .then((response) => {
          this.supplierMsa = response.data.length ? response.data.find((msa) => msa.role === 'supplier') : null;
          this.customerMsa = response.data.length ? response.data.find((msa) => msa.role === 'customer') : null;
        })
        .catch((error) => {
          throw new Error(error);
        });
    },

    fetchConfiguredWorkCategories() {
      this.fetchingConfiguredWorkCategories = true;

      const route = this.form.counter_party_type === 'App\\Models\\CounterParty' ? 'counter-party' : 'organisation';

      window.axios
        .get(this.route(`${route}.configured-work-categories`, [
          this.form.counter_party_id,
          this.form.asset_id,
        ]))
        .then((response) => {
          this.configuredWorkCategories = response.data;
          this.fetchingConfiguredWorkCategories = false;
        })
        .catch((error) => {
          throw new Error(error);
        });
    },

    noCounterparty() {
      this.$inertia.visit(this.route('asset.create', 'master-service-agreement'));
    },
  },
};
</script>
